import { useState } from 'react';
import { useTheme } from 'styled-components';

import { Modal } from '../../components';
import { Color } from '../../theme';
import { AlertProps, Button, ButtonProps, Checkbox, Icon, Text, alertIcons } from '../../ui';
import {
  StyledAlertIcon,
  StyledButtonsWrapper,
  StyledCloseButtonWrapper,
  StyledContainer,
  StyledHeaderWrapper,
  StyledTextContainer,
  StyledWrapper,
} from './Confirm.style';

export interface ConfirmLabels {
  title: string;
  description?: string;
  remember?: string;
  confirmButton: string;
  cancelButton?: string;
  extraButtons?: {
    label: string;
    variant: ButtonProps['variant'];
    onClick: () => void;
  }[];
}

export interface ConfirmProps {
  type?: AlertProps['type'];
  closeOnOverlayClick?: boolean;
  labels: ConfirmLabels;
  extraButtons?: {
    variant?: ButtonProps['variant'];
    label: string;
    onClick: (remember: boolean) => void;
  }[];
  onConfirm: (remember: boolean) => void;
  onCancel?: () => void;
  onDismiss?: () => void;
  closeButton?: boolean;
  align?: 'left' | 'right';
}

export function Confirm({
  align,
  closeButton = true,
  closeOnOverlayClick = false,
  extraButtons,
  labels,
  onCancel = () => null,
  onConfirm = () => null,
  onDismiss,
  type,
}: ConfirmProps) {
  const theme = useTheme();
  const [remember, setRemember] = useState(false);
  const onConfirmHandler = () => {
    onConfirm(remember);
  };

  return (
    <Modal size={'Large'} onOverlayClick={closeOnOverlayClick ? onDismiss ?? onCancel : undefined}>
      <StyledContainer>
        {type && alertIcons[type] && (
          <StyledAlertIcon type={type}>
            <Icon name={alertIcons[type]} size={'Medium'} />
          </StyledAlertIcon>
        )}

        <StyledWrapper>
          <StyledHeaderWrapper>
            <StyledTextContainer>
              <Text size={'Heading'}>{labels.title}</Text>

              {labels.description && (
                <Text size={'Body'} color={Color.Neutrals.Text}>
                  {labels.description}
                </Text>
              )}
            </StyledTextContainer>

            {labels.remember && (
              <Checkbox
                checked={remember}
                label={labels.remember}
                onClick={(checked) => setRemember(checked)}
              />
            )}
          </StyledHeaderWrapper>

          <StyledButtonsWrapper $align={align}>
            {labels.cancelButton && (
              <Button
                variant={'outline'}
                onClick={onCancel}
                label={labels.cancelButton}
                size={'Small'}
              />
            )}

            {extraButtons?.map((button, index) => (
              <Button
                key={index}
                variant={button.variant}
                onClick={() => button.onClick(remember)}
                label={button.label}
                size={'Small'}
              />
            ))}
            <Button onClick={onConfirmHandler} label={labels.confirmButton} size={'Small'} />
          </StyledButtonsWrapper>

          {closeButton && (
            <StyledCloseButtonWrapper>
              <Icon
                name="cross"
                size="Small"
                color={theme.Color.Neutrals.Gray}
                onClick={onDismiss ?? onCancel}
                highlightColor={theme.Color.Neutrals.Text}
                aria-label={'Close'}
              />
            </StyledCloseButtonWrapper>
          )}
        </StyledWrapper>
      </StyledContainer>
    </Modal>
  );
}
